import React from "react";
import { ChevronUp, ChevronDown, Search } from "lucide-react";
import PaginationControl from "./PaginationControl";

export interface GridColumn<T> {
  key: string;
  header: string;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  /** Backend sort field name — defaults to `key` when omitted. */
  sortKey?: string;
}

interface Props<T> {
  columns: GridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyIcon?: React.ReactNode;
  emptyText?: string;
  rowStyle?: (row: T) => React.CSSProperties;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;

  sortField?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (field: string) => void;

  searchValue?: string;
  onSearchChange?: (q: string) => void;
  searchPlaceholder?: string;

  pageSize?: number;
  onPageSizeChange?: (n: number) => void;

  selectedRowIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  toolbarActions?: React.ReactNode;
}

const TH: React.CSSProperties = {
  padding: "11px 16px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  color: "var(--nx-text-muted)",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid var(--nx-border)",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "11px 16px",
  fontSize: 13,
  verticalAlign: "middle",
  color: "var(--nx-text-primary)",
  whiteSpace: "nowrap",
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyIcon,
  emptyText = "No data.",
  rowStyle,
  page,
  totalPages,
  onPageChange,
  sortField,
  sortDir,
  onSortChange,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  pageSize,
  onPageSizeChange,
  selectedRowIds,
  onSelectionChange,
  toolbarActions,
}: Props<T>) {
  const selectable = onSelectionChange !== undefined;
  const selectedSet = new Set(selectedRowIds ?? []);
  const allVisibleSelected =
    selectable &&
    rows.length > 0 &&
    rows.every((row) => selectedSet.has(rowKey(row)));

  const toggleAllSelection = () => {
    if (!onSelectionChange) return;
    if (allVisibleSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(rows.map((row) => rowKey(row)));
    }
  };

  const hasPagination =
    totalPages !== undefined &&
    totalPages > 1 &&
    onPageChange !== undefined &&
    page !== undefined;

  const showToolbar =
    onSearchChange !== undefined ||
    onPageSizeChange !== undefined ||
    toolbarActions !== undefined ||
    hasPagination;

  return (
    <>
      {showToolbar && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "12px 16px",
            borderBottom: "1px solid var(--nx-border)",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {onSearchChange && (
              <div style={{ position: "relative" }}>
                <Search
                  size={13}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--nx-text-muted)",
                  }}
                />
                <input
                  value={searchValue ?? ""}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  style={{
                    padding: "7px 10px 7px 30px",
                    background: "var(--nx-surface-3)",
                    border: "1px solid var(--nx-border)",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "var(--nx-text-primary)",
                    outline: "none",
                    width: 220,
                  }}
                />
              </div>
            )}
            {onPageSizeChange && (
              <select
                value={pageSize ?? 20}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                style={{
                  padding: "7px 10px",
                  background: "var(--nx-surface-3)",
                  border: "1px solid var(--nx-border)",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "var(--nx-text-primary)",
                  outline: "none",
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {toolbarActions}
            {hasPagination && (
              <PaginationControl
                page={page!}
                totalPages={totalPages!}
                onPageChange={onPageChange!}
              />
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, display: "flex", justifyContent: "center" }}>
          <span
            style={{
              width: 24,
              height: 24,
              border: "2px solid var(--nx-border)",
              borderTopColor: "#1A6CF6",
              borderRadius: "50%",
              animation: "nx-spin 0.7s linear infinite",
              display: "inline-block",
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          {emptyIcon && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              {emptyIcon}
            </div>
          )}
          <div style={{ fontSize: 14, color: "var(--nx-text-muted)" }}>
            {emptyText}
          </div>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {selectable && (
                  <th style={{ ...TH, width: 44, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllSelection}
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {columns.map((col) => {
                  const key = col.sortKey ?? col.key;
                  const isActive = sortField === key;
                  return (
                    <th
                      key={col.key}
                      style={{
                        ...TH,
                        ...col.headerStyle,
                        cursor:
                          col.sortable && onSortChange ? "pointer" : undefined,
                        userSelect:
                          col.sortable && onSortChange ? "none" : undefined,
                      }}
                      onClick={
                        col.sortable && onSortChange
                          ? () => onSortChange(key)
                          : undefined
                      }
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {col.header}
                        {col.sortable &&
                          isActive &&
                          (sortDir === "desc" ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronUp size={12} />
                          ))}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowId = rowKey(row);
                const selected = selectedSet.has(rowId);
                return (
                  <tr
                    key={rowId}
                    style={{
                      borderBottom: "1px solid var(--nx-border)",
                      ...rowStyle?.(row),
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--nx-surface-2)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "")
                    }
                  >
                    {selectable && (
                      <td style={{ ...TD, width: 44, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            if (!onSelectionChange) return;
                            const current = selectedRowIds ?? [];
                            if (selected) {
                              onSelectionChange(
                                current.filter((id) => id !== rowId),
                              );
                            } else {
                              onSelectionChange([...current, rowId]);
                            }
                          }}
                          aria-label={`Select row ${rowId}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} style={{ ...TD, ...col.cellStyle }}>
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default DataTable;
